// src/components/Stimuli/StimulusDetail.jsx
import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import Layout from '../Layout/Layout'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiFileText, FiArrowLeft, FiRefreshCw } = FiIcons

const StimulusDetail = () => {
  const { id } = useParams()
  const [stimulus, setStimulus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchStimulus = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data, error, status } = await supabase
          .from('stimuli')
          .select('*')
          .eq('id', id)
          .single()

        if (error && status !== 406) {
          console.error('Error fetching stimulus:', { status, error })
          setError('Could not load this stimulus.')
        } else {
          setStimulus(data)
        }
      } catch (err) {
        console.error('Unexpected error fetching stimulus:', err)
        setError('Unexpected error while loading this stimulus.')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchStimulus()
    }
  }, [id])

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12 text-gray-500">
          Loading stimulus...
        </div>
      </Layout>
    )
  }

  // Error / not found state
  if (error || !stimulus) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto py-8 space-y-4">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
            Back to dashboard
          </Link>

          <div className="bg-white border border-red-100 text-red-700 p-4 rounded-lg">
            {error || 'Stimulus not found.'}
          </div>
        </div>
      </Layout>
    )
  }

  // Helper to render key/value pairs without assuming schema
  const renderFieldValue = (key, value) => {
    if (value === null || value === undefined || value === '') return null

    // Simple formatting for dates
    if (key === 'created_at' || key === 'updated_at') {
      return new Date(value).toLocaleString()
    }

    if (typeof value === 'object') {
      return <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(value, null, 2)}</pre>
    }

    return String(value)
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-8 space-y-6">
        {/* Back link */}
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
            Back to dashboard
          </Link>
        </div>

        {/* Header card */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <SafeIcon
                icon={FiFileText}
                className="w-5 h-5 text-gray-500"
              />
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  {stimulus.title || 'Untitled stimulus'}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                  {stimulus.source_type && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
                      {stimulus.source_type === 'uploaded_file'
                        ? 'Uploaded file'
                        : stimulus.source_type === 'pasted_text'
                        ? 'Pasted text'
                        : stimulus.source_type}
                    </span>
                  )}
                  {stimulus.mime_type && <span>{stimulus.mime_type}</span>}
                  {stimulus.created_at && (
                    <span>
                      Created:{' '}
                      {new Date(stimulus.created_at).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Primary action */}
            <div className="flex flex-col items-end space-y-2">
              <Link
                to={`/lesson/new?stimulusId=${stimulus.id}`}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                Generate new lesson from this stimulus
              </Link>
            </div>
          </div>
        </div>

        {/* All stimulus fields */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Stimulus details
          </h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {Object.entries(stimulus).map(([key, value]) => {
              const rendered = renderFieldValue(key, value)
              if (!rendered) return null
              return (
                <div key={key}>
                  <dt className="font-medium text-gray-600">
                    {key.replace(/_/g, ' ')}
                  </dt>
                  <dd className="text-gray-900 mt-1 break-words">
                    {rendered}
                  </dd>
                </div>
              )
            })}
          </dl>
        </div>
      </div>
    </Layout>
  )
}

export default StimulusDetail